import { useEffect, useState } from 'react';

import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { useAuth } from '@client/context/auth/AuthContext';
import ClientTimestampConverter from '@client/helpers/conversions/clientTimestampConverter';
import { apiClient } from '@client/helpers/dataFetching/apiClient';
import { useFileStorageHelper } from '@client/pages/intake/onboarding/onboardingSteps/intakeForm/hooks/useFileStorageHelper';
import { db } from '@config/firebase';
import { type PBConfirmation, type PersonalTaxDeclarationType } from '@customTypes/personalTax';
import { type NestedPartial } from '@customTypes/utilityTypes';
import { convertObjectToDotNotatedObjectProperties } from '@helpers/dataTransformation/convertObjectToDotNotatedObject';
import { getYear } from 'date-fns';

const DEFAULT_PERSONAL_TAX_INFO: PersonalTaxDeclarationType = {
  isVisible: false,
  applicableFor: 'user',
  isSubmitted: false,
  createdAt: new Date(),
  lastUpdatedAt: new Date(),
  fiscalYear: getYear(new Date()),
  basicInfo: {},
  familyInfo: {},
  fichesInfo: {},
  realEstateInfo: {},
};

const mergePersonalInfoData = (
  defaultData: PersonalTaxDeclarationType,
  fetchedData: Partial<PersonalTaxDeclarationType>,
): PersonalTaxDeclarationType => ({
  ...defaultData,
  ...fetchedData,
});

export const usePersonalTaxData = () => {
  const { user } = useAuth();
  const { uploadFile } = useFileStorageHelper();
  const [personalTaxInfo, setPersonalTaxInfo] = useState<PersonalTaxDeclarationType>({
    ...DEFAULT_PERSONAL_TAX_INFO,
  });
  const isEmpty = Object.keys(personalTaxInfo).length === 0;
  const isVisible = !!personalTaxInfo.isVisible;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;

    const personalTaxColRef = collection(db, `users/${user.uid}/personalTaxReturns`);
    const personalTaxDocQuery = query(
      personalTaxColRef,
      where('isVisible', '==', true),
      orderBy('createdAt', 'desc'),
      limit(1),
    ).withConverter<PersonalTaxDeclarationType>(new ClientTimestampConverter());

    const unsubscribe = onSnapshot(
      personalTaxDocQuery,
      querySnapshot => {
        try {
          if (!querySnapshot.empty) {
            const data = querySnapshot.docs[0].data();
            const id = querySnapshot.docs[0].id;

            // console.log("personal tax id", id);
            const mergedData = mergePersonalInfoData(DEFAULT_PERSONAL_TAX_INFO, { ...data, id });

            setPersonalTaxInfo(mergedData);
          }
        } catch (error) {
          console.error('Error fetching personal tax data:', error);
        } finally {
          setLoading(false);
        }
      },
      error => {
        console.error('Error fetching personal tax data:', error);
        setLoading(false);
      },
    );

    // Clean up the listener when component unmounts
    return () => unsubscribe();
  }, [user?.uid]);

  /**
   *
   * @param data  - data to update in firestore
   * @param id  - id of the document to update
   */
  const updatePersonalTaxInfoDocument = async (
    data: NestedPartial<PersonalTaxDeclarationType> | Record<string, unknown>,
    id?: string,
  ) => {
    // TODO what if the document doesn't exist?
    if (!id) {
      throw new Error('No document id provided');
    }

    const dataToUpdate = {
      ...data,
      lastUpdatedAt: Timestamp.now().toDate(),
    };
    // convert object to dot notation
    const dotNotatedData = convertObjectToDotNotatedObjectProperties({
      ...dataToUpdate,
    });
    const onboardingDoc = doc(db, `users/${user?.uid}/personalTaxReturns/${id}`);
    // console.log("updating data...", dotNotatedData);
    await updateDoc(onboardingDoc, dotNotatedData);
  };

  const uploadFiches = async (fiches: File[], bucketPath: string) => {
    try {
      if (!fiches.length) return;

      // console.log("uploading fiches...");
      const uploadedFiches = await Promise.all(
        fiches.map(fiche => {
          return uploadFile(bucketPath, fiche.name, fiche, {
            keepFileName: false, // This will add a timestamp to the filename to prevent overwriting
            originalFilename: true,
          });
        }),
      );

      return uploadedFiches ?? [];
    } catch (error) {
      // console.error("Error uploading fiches:", error);
      throw error;
    }
  };

  const handlePersonalTaxSubmit = async (data: PBConfirmation) => {
    try {
      const response = await apiClient.put(
        `/api/users/${user?.uid}/personalTaxReturns/${personalTaxInfo.id}/handleCompletionRequest`,
        {
          data,
          // Increased timeout to 20 seconds due to the possible long time to upload files to drive
          timeout: 20000,
        },
      );

      if (!response) throw new Error('Error submitting personal tax info');

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  const getFiscalFichesFromComax = async (fiscalYear?: number, nationalRegistryNumber?: string) => {
    try {
      const fiscalYearQuery = fiscalYear ?? personalTaxInfo.fiscalYear ?? getYear(new Date());

      const nationalRegistryNumberQuery =
        nationalRegistryNumber ?? personalTaxInfo.basicInfo?.nationalRegistryNumber ?? undefined;

      const response = await apiClient.get(
        `/api/users/${user?.uid}/drive/getFiscalFichesFromComax?userId=${user?.uid}&fiscalYear=${fiscalYearQuery}&nationalRegistryNumber=${nationalRegistryNumberQuery}`,
      );

      const comaxFilesList = response?.data ? (response.data as string[]) : [];

      return comaxFilesList;
    } catch (error: any) {
      throw error;
    }
  };

  return {
    uploadFiches,
    handlePersonalTaxSubmit,
    personalTaxInfo,
    isEmpty,
    isVisible,
    loading,
    updatePersonalTaxInfoDocument,
    getFiscalFichesFromComax,
  };
};
